import { t } from '@hana/i18n';
import './CostDock.i18n';
import { useState } from 'react';
import { IconCheck, IconBuildingStore, IconArrowBackUp, IconX } from '@tabler/icons-react';
import { Button, useToast } from '@hana/ui';
import { AN_AGE, AN_KPI, AN_MONTHS, AN_REC, AN_SVC_TOTAL, AN_TASKS, f1, stepGroups, svcDonut, topGroups, topPartners, type DockBar } from './view';
import type { SupplierInvoice, Voucher } from './types';
import s from './cost.module.css';

// Mint-Cost bảng phải 372 (mở sẵn ở 1920): Phân tích · Duyệt · Việc. Chỉ đọc; nút ghi báo toast.

export type DockTab = 'an' | 'apr' | 'task';

function Bars({ rows }: { rows: readonly DockBar[] }) {
  return (
    <>
      {rows.map((a) => (
        <div key={a.k} className={s.dkRow}>
          <span>{a.k}</span>
          <div className={s.dkTrack}><div className={s[`t_${a.tone}`]} style={{ width: a.w }} /></div>
          <b>{a.v}</b>
        </div>
      ))}
    </>
  );
}

export function CostDock({ invoices, vouchers, onClose, onPickPartner, onPickRequests }: { invoices: readonly SupplierInvoice[]; vouchers: readonly Voucher[]; onClose: () => void; onPickPartner: (p: string) => void; onPickRequests: () => void }) {
  const toast = useToast();
  const [tab, setTab] = useState<DockTab>('an');
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  const mine = vouchers.filter((v) => v.status === 'kt');
  const steps = stepGroups(vouchers);
  const svc = svcDonut();
  const groups = topGroups(invoices);
  const partners = topPartners(invoices);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const tabs: readonly { key: DockTab; label: string; n?: number }[] = [{ key: 'an', label: 'Phân tích' }, { key: 'apr', label: 'Duyệt', n: mine.length }, { key: 'task', label: 'Việc', n: AN_TASKS.length }];

  return (
    <div className={s.dock}>
      <div className={s.dkBar}>
        <div className={s.dkTabs} role="tablist" aria-label="Cột phải">
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>
              {t.label} {t.n !== undefined && <span>{t.n}</span>}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" aria-label="Đóng bảng" title="Đóng bảng" icon={<IconX size={17} aria-hidden="true" />} onClick={onClose} />
      </div>

      {tab === 'an' && (
        <>
          <section className={s.dkSec}>
            <h2>{t('cdk.1')}</h2>
            <div className={s.kpis}>
              {AN_KPI.map((k) => (
                <div key={k.k}>
                  <div className={s.kLab}>{k.k}</div>
                  <b className={k.tone ? s[k.tone] : undefined}>{k.v}</b> <span className={s.kLab}>{k.u}</span>
                </div>
              ))}
            </div>
          </section>

          <section className={s.dkSec} aria-label="Đề nghị theo bước duyệt">
            <div className={s.dkHead}><h2>{t('cdk.2')}</h2><span>{vouchers.length} phiếu</span></div>
            <div className={s.stepBar} role="img" aria-label={`Đề nghị thanh toán theo bước: ${steps.map((x) => `${x.k} ${x.n}`).join(', ')}`}>
              {steps.map((x) => <div key={x.k} title={`${x.k} · ${x.n}`} className={s[`t_${x.tone}`]} style={{ flex: `${x.n} 1 0` }} />)}
            </div>
            <div className={s.stepList}>
              {steps.map((x) => (
                <button key={x.k} type="button" onClick={onPickRequests}>
                  <i className={s[`t_${x.tone}`]} />
                  <span>{x.k}</span>
                  <b>{x.n}</b>
                </button>
              ))}
            </div>
          </section>

          <section className={s.dkSec}>
            <div className={s.dkHead}><h2>{t('cdk.3')}</h2><span>tr ₫</span></div>
            <Bars rows={AN_AGE} />
          </section>

          <section className={s.dkSec} aria-label="Chi theo dịch vụ">
            <div className={s.dkHead}><h2>{t('cdk.4')}</h2><span>tr ₫</span></div>
            <div className={s.donutRow}>
              <svg role="img" aria-label={`Chi theo dịch vụ tháng 10 (tr ₫): ${svc.map((d) => `${d.k} ${d.v}`).join(', ')}; tổng ${AN_SVC_TOTAL.toLocaleString('vi-VN')}`} width="108" height="108" viewBox="0 0 132 132">
                <circle cx="66" cy="66" r="52" fill="none" className={s.donutBg} strokeWidth="16" />
                {svc.map((d) => <circle key={d.k} cx="66" cy="66" r="52" fill="none" className={s[`st_${d.tone}`]} strokeWidth="16" strokeDasharray={d.dash} strokeDashoffset={d.off} transform="rotate(-90 66 66)" />)}
                <text x="66" y="68" textAnchor="middle" fontSize="18" fontWeight="550" className={s.donutTx}>{AN_SVC_TOTAL.toLocaleString('vi-VN')}</text>
                <text x="66" y="84" textAnchor="middle" fontSize="11" className={s.donutLb}>tr ₫</text>
              </svg>
              <div className={s.legendCol}>
                {svc.map((d) => (
                  <span key={d.k}><i className={s[`t_${d.tone}`]} /><span>{d.k}</span><b>{d.v}</b><em>{d.p}</em></span>
                ))}
              </div>
            </div>
          </section>

          <section className={s.dkSec}>
            <h2 title="Số dòng chi dự kiến đã có HĐ mua vào / tổng dòng cần HĐ · kỳ 10/2026">{t('cdk.5')}</h2>
            <Bars rows={AN_REC} />
          </section>

          <section className={s.dkSec}>
            <div className={s.dkHead}><h2>{t('cdk.6')}</h2><span>tr ₫</span></div>
            <div className={s.mon}>
              {AN_MONTHS.map((m) => (
                <div key={m.k}>
                  <span>{m.v.toLocaleString('vi-VN')}</span>
                  <div title={`${m.k} · ${m.v.toLocaleString('vi-VN')} tr`} className={m.now ? s.t_pri : s.t_blue} style={{ height: m.h }} />
                  <span className={s.kLab}>{m.k}</span>
                </div>
              ))}
            </div>
          </section>

          <section className={s.dkSec}>
            <h2>{t('cdk.7')}</h2>
            {groups.map((a) => (
              <div key={a.k} className={`${s.dkRow} ${s.dkRowType}`}>
                <span>{a.k}</span>
                <div className={s.dkTrack}><div className={s.t_blue} style={{ width: a.w }} /></div>
                <b>{a.v}</b>
              </div>
            ))}
          </section>

          <section className={s.dkSec}>
            <h2><IconBuildingStore size={16} aria-hidden="true" />{t('cdk.8')}</h2>
            <div role="group" aria-label={`Đối tác lớn nhất tháng 10 (tr ₫): ${partners.map((x) => `${x.k} ${x.v}`).join(', ')}`}>
              {partners.map((a) => (
                <button key={a.k} type="button" className={s.ncc} aria-label={`Lọc HĐ ${a.k} · ${a.n} HĐ · ${a.v} tr`} onClick={() => onPickPartner(a.k)}>
                  <span>{a.k}</span>
                  <div className={s.dkTrack}><div className={s.t_blue} style={{ width: a.w }} /></div>
                  <em>{a.n} HĐ</em>
                  <b>{a.v}</b>
                </button>
              ))}
            </div>
          </section>
        </>
      )}

      {tab === 'apr' && (
        <>
          {mine.map((v) => {
            const blocked = v.maker === 'LIENNTK';
            return (
              <div key={v.no} className={s.aprCard}>
                <div className={s.aprTop}><b className={s.no}>{v.no}</b><b>{f1(v.total)} tr</b></div>
                <div className={s.aprSub}>{v.partner} · {v.job}</div>
                <div className={s.kLab}>{t('cdk.9')}</div>
                {blocked && <div className={s.warn}>{t('cdk.10')}</div>}
                <div className={s.aprAct}>
                  <Button variant="primary" size="sm" disabled={blocked} icon={<IconCheck size={16} aria-hidden="true" />} onClick={noWrite}>{t('cdk.11')}</Button>
                  <Button size="sm" icon={<IconArrowBackUp size={16} aria-hidden="true" />} onClick={noWrite}>{t('cdk.12')}</Button>
                </div>
              </div>
            );
          })}
          {mine.length === 0 && <div className={s.dkNone}>{t('cdk.13')}</div>}
        </>
      )}

      {tab === 'task' &&
        AN_TASKS.map((t, i) => (
          <div key={t.code} className={`${s.task} ${done.has(i) ? s.taskDone : ''}`}>
            <input type="checkbox" aria-label={`Xong: ${t.k}`} checked={done.has(i)} onChange={() => setDone((p) => { const n = new Set(p); if (!n.delete(i)) n.add(i); return n; })} />
            <div>
              <div><span className={s.code}>{t.code}</span>{t.k}</div>
              <div className={t.urgency === 2 ? s.err : t.urgency === 1 ? s.warn : s.kLab}>{t.due}</div>
            </div>
          </div>
        ))}
    </div>
  );
}
